import { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { SITE, SERVICE_IDS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const location = useLocation();
  const prefill = location.state?.service || "";

  const [form, setForm] = useState({ name: "", phone: "", email: "", service: prefill, message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiry`, {
        ...form,
        email: form.email || null,
        source: "contact",
      });
      setSent(true);
      toast.success(t.contactPage.form.success, { description: t.contactPage.form.successDesc });
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error(t.contactPage.form.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-electric focus:border-electric transition-colors duration-200";
  const labelCls = "block text-xs font-semibold text-slate-600 mb-1.5";

  const infoItems = [
    { icon: Phone, label: t.contactPage.info.phoneLabel, value: SITE.phoneDisplay, href: SITE.phoneHref, testid: "contact-direct-call-link", external: false },
    { icon: MessageCircle, label: t.contactPage.info.whatsappLabel, value: "WhatsApp chat", href: SITE.whatsappHref, testid: "contact-direct-whatsapp-link", external: true },
    { icon: Mail, label: t.contactPage.info.emailLabel, value: SITE.email, href: `mailto:${SITE.email}`, testid: "contact-direct-email-link", external: false },
    { icon: MapPin, label: t.contactPage.info.addressLabel, value: SITE.address, testid: "contact-direct-address" },
    { icon: Clock, label: t.contactPage.info.hoursLabel, value: SITE.hours, testid: "contact-direct-hours" },
  ];

  return (
    <div>
      {/* Header banner */}
      <section className="relative bg-navy text-white overflow-hidden" data-testid="contact-header">
        <div className="absolute inset-0 grid-pattern-dark opacity-60" />
        <div className="absolute -bottom-32 -right-24 w-[420px] h-[420px] rounded-full bg-electric/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-sky-300 font-semibold">{t.contactPage.eyebrow}</p>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight" data-testid="contact-title">{t.contactPage.title}</h1>
            <p className="mt-4 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">{t.contactPage.subtitle}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Form */}
          <Reveal className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="rounded-2xl border border-slate-200 bg-white p-7 sm:p-10 shadow-card"
              data-testid="contact-form"
            >
              {sent && (
                <div className="mb-6 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800" data-testid="contact-form-success">
                  {t.contactPage.form.success} — {t.contactPage.form.successDesc}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>{t.contactPage.form.name}</label>
                  <input required minLength={2} value={form.name} onChange={set("name")} placeholder={t.contactPage.form.namePh} className={inputCls} data-testid="contact-form-name" />
                </div>
                <div>
                  <label className={labelCls}>{t.contactPage.form.phone}</label>
                  <input required type="tel" value={form.phone} onChange={set("phone")} placeholder={t.contactPage.form.phonePh} className={inputCls} data-testid="contact-form-phone" />
                </div>
              </div>
              <div className="mt-5">
                <label className={labelCls}>{t.contactPage.form.email}</label>
                <input type="email" value={form.email} onChange={set("email")} placeholder={t.contactPage.form.emailPh} className={inputCls} data-testid="contact-form-email" />
              </div>
              <div className="mt-5">
                <label className={labelCls}>{t.contactPage.form.service}</label>
                <select required value={form.service} onChange={set("service")} className={inputCls} data-testid="contact-form-service-select">
                  <option value="" disabled>{t.contactPage.form.servicePh}</option>
                  {SERVICE_IDS.map((id) => (
                    <option key={id} value={t.servicesPage.items.find((s) => s.id === id).title}>
                      {t.servicesPage.items.find((s) => s.id === id).title}
                    </option>
                  ))}
                  <option value={t.contactPage.form.other}>{t.contactPage.form.other}</option>
                </select>
              </div>
              <div className="mt-5">
                <label className={labelCls}>{t.contactPage.form.message}</label>
                <textarea required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder={t.contactPage.form.messagePh} className={inputCls} data-testid="contact-form-message" />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-form-submit-button"
                className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-electric px-8 py-3.5 text-sm font-semibold text-white hover:bg-blue-700 active:scale-[0.98] transition-all duration-200 disabled:opacity-60"
              >
                <Send className="w-4 h-4" />
                {sending ? t.contactPage.form.sending : t.contactPage.form.submit}
              </button>
            </form>
          </Reveal>

          {/* Info */}
          <Reveal delay={0.12} className="lg:col-span-5">
            <div className="rounded-2xl bg-navy text-white p-8 sm:p-10 h-full" data-testid="contact-info-card">
              <h2 className="font-display text-xl font-bold">{t.contactPage.info.title}</h2>
              <ul className="mt-7 space-y-6">
                {infoItems.map((item, i) => {
                  const Icon = item.icon;
                  const content = (
                    <>
                      <span className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-amber2" />
                      </span>
                      <span>
                        <span className="block text-xs font-mono uppercase tracking-wider text-slate-400">{item.label}</span>
                        <span className="block mt-1 text-sm font-medium leading-relaxed break-words">{item.value}</span>
                      </span>
                    </>
                  );
                  return (
                    <li key={i} className="flex items-start gap-4">
                      {item.href ? (
                        <a href={item.href} {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})} data-testid={item.testid} className="flex items-start gap-4 hover:opacity-80 transition-opacity duration-200">
                          {content}
                        </a>
                      ) : (
                        <div data-testid={item.testid} className="flex items-start gap-4">
                          {content}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
